// Rekit uses a new approach to organizing actions and reducers. That is
// putting related actions and reducers in one file. See more at:
// https://medium.com/@nate_wang/a-new-approach-for-managing-redux-actions-91c26ce8b5da

import {
  SEND_MESSAGE,
  DELETE_MESSAGE,
  TRAIN_MESSAGE,
  RECEIVE_MESSAGE,
  RECEIVE_MESSAGES,
  MARK_MESSAGE,
  CLEAR_MESSAGES,
  UPDATE_PAGINATION,
  CHAT_CONNECTED,
  USER_TYPING_MESSSAGE,
  USER_STOP_TYPING_MESSSAGE,
} from './constants';
import { getStarredMessages } from '~/features/chat/redux/actions';
import Naluri from '~/common/api/naluri';
import NaluriSuggest from '~/common/api/naluriSuggest';
import uuidv4 from 'uuid/v4';
import { delay } from '~/common/utils';
import { OrderedMap } from 'immutable';

export function sendMessage(body, replyToId) {
  return (dispatch, getState) => {
    const channel = getState().chat.chatState.channel;
    const id = uuidv4();
    const channel_id = getState().chat.chatState.streamId;
    const user_id = getState().session.authenticated.id;
    const timestamp = Date.now();

    const message = Object.assign(
      {},
      {
        id,
        channel_id,
        user_id,
        timestamp,
        mode: 'me',
        type: 'text',
        body: body,
        reply_to_id: replyToId,
      },
    );

    channel.push('new:message', message);
  };
}

export const getMessages = (firstPage = false) => (dispatch, getState) => {
  const { chat } = getState();
  const page = chat.chatState.pagination && !firstPage ? chat.chatState.pagination.page + 1 : 1;
  return Naluri.get(`/api/expert/${chat.chatState.streamId}/history?page=${page}`)
    .then(response => {
      dispatch(markReplied(response.mark));
      dispatch(receiveMessages(response.messages));
      dispatch(updatePagination(response.pagination));
      dispatch(getStarredMessages(chat.chatState.streamId));

      return response;
    })
    .catch(error => {
      console.log(error);
    });
};

export function deleteMessage(id) {
  return (dispatch, getState) => {
    let streamId = getState().chat.chatState.streamId;

    Naluri.delete(`/${streamId}/message/${id}`)
      .then(response => {
        dispatch({ type: DELETE_MESSAGE, id });
      })
      .catch(xhr => {
        dispatch({ type: DELETE_MESSAGE, id });
      });
  };
}

export function trainMessage(id, talk) {
  return (dispatch, getState) => {
    let streamId = getState().chat.chatState.streamId;

    Naluri.post(`/${streamId}/message/${id}/train`, { actual_label: talk }).then(response => {
      dispatch({
        type: TRAIN_MESSAGE,
        payload: response,
      });
      return response;
    });
  };
}

export const receiveMessages = messages => ({
  type: RECEIVE_MESSAGES,
  payload: messages,
});

export const markReplied = mark => ({
  type: MARK_MESSAGE,
  payload: mark,
});

export const updatePagination = pagination => ({
  type: UPDATE_PAGINATION,
  payload: pagination,
});

export function sendTypingStatus() {
  return (dispatch, getState) => {
    const { session, chat } = getState();
    const { chatState } = chat;
    const { channel, streamId } = chatState;
    const user = session.authenticated;

    delay(function() {
      channel.push('user:typing', {
        id: user.id,
        name: user.name,
        // photo: user.photo,
        channel_id: streamId,
      });
    }, 1000);
  };
}

export function clearMessages() {
  return { type: CLEAR_MESSAGES };
}

export function getTextSuggestions(message) {
  return dispatch => {
    const params = {
      query: message.body,
      lang: 'en',
      sessionId: message.channel_id,
      contexts: [{ name: 'env', parameters: { env: 'production' } }],
    };

    NaluriSuggest.post('/', params).then(response => {
      const suggestions = response.result.fulfillment.messages;
      const newMessage = Object.assign(message, { suggestions });

      dispatch({ type: RECEIVE_MESSAGE, message: newMessage });

      return suggestions;
    });
  };
}

export function sendFile(file) {
  return (dispatch, getState) => {
    const { size, name, type } = file;

    Naluri.post('/api/uploads/sign', { size, name, type }).then(response => {
      let xhr = new XMLHttpRequest();
      xhr.open('PUT', response.signed_url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4 && xhr.status === 200) {
          let channel = getState().chat.chatState.channel;

          let message = {
            id: uuidv4(),
            channel_id: getState().chat.chatState.streamId,
            user_id: getState().chat.chatState.userId,
          };

          if (type.includes('image/')) {
            message.type = 'image';
            message.image = response.url;
          } else if (type.includes('video/')) {
            message.type = 'video';
            message.video = response.url;
          } else {
            message.type = 'file';
            message.attachment = response.url;
          }

          delay(function() {}, 0);
          dispatch({ type: SEND_MESSAGE, message: message });
          channel.push('new:message', message);
        }
      };
    });
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case SEND_MESSAGE:
      return {
        ...state,
        messages: state.messages.set(action.message.id, action.message),
      };

    case MARK_MESSAGE:
      return {
        ...state,
        chatState: {
          ...state.chatState,
          mark: action.payload,
        },
        messagesPending: false,
      };

    case RECEIVE_MESSAGES:
      let newList = state.messages;

      action.payload.forEach(message => {
        newList = newList.set(message.id, message);
      });

      return {
        ...state,
        messages: newList,
      };

    case DELETE_MESSAGE:
      let newMessages = state.messages;
      if (Array.isArray(action.id)) {
        action.id.forEach(msgId => {
          newMessages = newMessages.remove(msgId);
        });
      } else {
        newMessages = newMessages.remove(action.id);
      }
      return {
        ...state,
        messages: newMessages,
      };

    case UPDATE_PAGINATION:
      return {
        ...state,
        chatState: {
          ...state.chatState,
          pagination: action.payload,
        },
      };

    case TRAIN_MESSAGE:
      return {
        ...state,
        messages: state.messages.set(action.payload.id, action.payload),
      };

    case CHAT_CONNECTED:
      let newState = state.messages;

      if (action.history.messages && action.history.messages.length) {
        if (newState.size !== 0 && action.streamId !== newState.values().next().value.channel_id) {
          newState = OrderedMap();
        }
        action.history.messages.forEach(message => {
          newState = newState.set(message.id, message);
        });
      }

      return {
        ...state,
        messages: newState,
      };

    case USER_TYPING_MESSSAGE:
      let typingUsers = state.chatState.typingUsers || [];

      if (action.user.id !== state.userId && !typingUsers.find(u => u.id === action.user.id)) {
        return {
          ...state,
          chatState: {
            ...state.chatState,
            isTyping: true,
            typingUsers: typingUsers.concat([action.user]),
          },
        };
      } else {
        return state;
      }

    case USER_STOP_TYPING_MESSSAGE:
      let typingUsersStop = state.chatState.typingUsers || [];

      if (action.user.id !== state.userId) {
        if (typingUsersStop.find(u => u.id === action.user.id)) {
          typingUsersStop = typingUsersStop.filter(u => u.id !== action.user.id);
        }
        if (typingUsersStop.length > 0) {
          return {
            ...state,
            chatState: {
              ...state.chatState,
              isTyping: true,
              typingUsers: typingUsersStop,
            },
          };
        } else {
          return {
            ...state,
            chatState: {
              ...state.chatState,
              isTyping: false,
              typingUsers: typingUsersStop,
            },
          };
        }
      } else {
        return state;
      }

    case RECEIVE_MESSAGE:
      let data = action.message;

      return {
        ...state,
        messages: state.messages.set(data.id, data),
      };

    case CLEAR_MESSAGES:
      return {
        ...state,
        messages: state.messages.clear(),
      };

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/features/chat/redux/messages.js